import { FC, useState, useEffect, useMemo } from "react";
import {
    IonCard,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonLabel,
    IonLoading,
    IonModal,
    IonPage,
    IonSegmentButton,
    IonTitle,
    IonToolbar,
    IonButtons,
    IonButton,
    useIonToast,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { FormProvider, useForm, useWatch, Controller } from "react-hook-form";
import { useIonRouter } from "@ionic/react";
import {
    shirtOutline,
    shirt,
    closeOutline,
} from "ionicons/icons";
import locationPitchIcon from "../../assets/images/icons/location-pitch.svg";
import venueStadiumIcon from "../../assets/images/icons/venue-stadium.svg";
import competitionTrophyIcon from "../../assets/images/icons/competition-trophy.svg";
import positionPlayerIcon from "../../assets/images/icons/position-player.svg";
import {
    EventName,
    FootballPosition,
    GoalEvent,
    MatchClaims,
    MatchLocation,
    MatchPhone,
    SelectedTeam,
} from "refsix-js-models";
import { makeShortName, updateTeamNameInEvents } from "refsix-core";
import { cloneDeep } from "lodash";

import { HeaderComponent, ColorPickerComponent } from "../../components";
import { useMatch, useMatches } from "../../components/hooks/match";
import { useProfile } from "../../components/hooks";
import { recalculateStats, updateMatch } from "../../services/matchService";
import { trackMatchEdited } from "../../services/analytics/analyticsService";
import { routes } from "../../route/constants";
import { RootState } from "../../redux/store";
import { ScoreCounter } from "../../components/ScoreCounter";
import { PositionDropdown } from "../../components/PositionDropdown";
import { IonFormSegment } from "../../components/r6ionic/R6IonSegment";
import { markAsTraining } from "../../services/matchTimingsService";
import { countGoalsForSide, setGoalsForSide, sideForLocation } from "./teamGoals";
import { NO_CLAIMS, seasonClaimTotals, trimClaimsToScore } from "./goalClaims";
import { ClaimRows } from "./ClaimRows";
import { SaveSplash } from "./SaveSplash";
import { earnedMoment, SaveMoment } from "./saveMoments";
import { submitMatchAndTagVenue } from "../../services/venueService";
import "./EditMatchDetails.css";

type ActivityType = "match" | "training";

interface EditMatchForm {
    activityType: ActivityType;
    myTeamName: string;
    myTeamShort: string;
    myTeamColor: string;
    opponentName: string;
    opponentShort: string;
    opponentColor: string;
    myTeamScore: number;
    opponentScore: number;
    // The goals and assists the user claims, never more than myTeamScore
    myClaims: MatchClaims;
    location: MatchLocation;
    venue: string;
    competition: string;
    playerPosition?: FootballPosition;
}

interface EditMatchDetailsProps {
    match: { params: { id: string } };
}

const GENERIC_WATCH_PLACEHOLDER = "?";

const EditMatchDetails: FC<EditMatchDetailsProps> = ({ match: routeMatch }) => {
    const { t } = useTranslation();
    const GENERIC_TEAM_NAMES = useMemo(() => [
        t("fixture.FixtureNew.homeTeam"),
        t("fixture.FixtureNew.awayTeam"),
        t("watch:team_title_home"),
        t("watch:team_title_away"),
        "Home",
        "Away",
    ], [t]);
    const { id } = useParams<{ id: string }>();
    const router = useIonRouter();
    const profile = useProfile();
    const matchPhone = useMatch(id);
    const matches = useMatches();
    const seasonDate = useSelector((state: RootState) => state.settings.settings.seasonDate);
    // The user's claims from this season's other matches, for the running total
    const seasonClaims = useMemo(() => seasonClaimTotals(matches || [], seasonDate, id), [matches, seasonDate, id]);

    const [loading, setLoading] = useState(!matchPhone);
    const [showColorPicker, setShowColorPicker] = useState(false);
    // The splash a save earned, and the score line it shows
    const [splash, setSplash] = useState<{ moment: SaveMoment; score: string }>();
    const [colorPickerTeam, setColorPickerTeam] = useState<MatchLocation>(MatchLocation.home);

    // Determine initial match location based on existing data or default to home
    const initialMatchLocation: MatchLocation = matchPhone?.matchLocation || MatchLocation.home;

    // Determine which team is "my team" based on matchLocation
    const getTeamMapping = (location: MatchLocation) => {
        if (location === MatchLocation.home) {
            return {
                myTeam: {
                    name: matchPhone?.homeTeam || "",
                    short: matchPhone?.homeTeamShort || "",
                    color: matchPhone?.homeColor || "",
                },
                opponent: {
                    name: matchPhone?.awayTeam || "",
                    short: matchPhone?.awayTeamShort || "",
                    color: matchPhone?.awayColor || "",
                },
            };
        } else {
            return {
                myTeam: {
                    name: matchPhone?.awayTeam || "",
                    short: matchPhone?.awayTeamShort || "",
                    color: matchPhone?.awayColor || "",
                },
                opponent: {
                    name: matchPhone?.homeTeam || "",
                    short: matchPhone?.homeTeamShort || "",
                    color: matchPhone?.homeColor || "",
                },
            };
        }
    };

    const initialMapping = getTeamMapping(initialMatchLocation);

    // Get scores by counting goal events directly (consistent with updateScoreEvents)
    const getScores = (location: MatchLocation) => {
        const homeScore = countGoalsForSide(matchPhone?.matchEvents, SelectedTeam.home);
        const awayScore = countGoalsForSide(matchPhone?.matchEvents, SelectedTeam.away);
        if (location === MatchLocation.home) {
            return { myScore: homeScore, opponentScore: awayScore };
        } else {
            return { myScore: awayScore, opponentScore: homeScore };
        }
    };

    const initialScores = getScores(initialMatchLocation);

    // Replace watch default team names with profile team name, strip other generic names to blank
    const stripGenericTeamName = (value: string | undefined, useProfileDefault?: boolean): string => {
        if (!value) return "";
        if (GENERIC_TEAM_NAMES.includes(value)) {
            return useProfileDefault ? (profile?.teamName ?? "") : "";
        }
        return value;
    };

    // Strip watch placeholder "?" to blank so validation catches it
    const stripWatchPlaceholder = (value: string | undefined): string => {
        if (!value || value === GENERIC_WATCH_PLACEHOLDER) return "";
        return value;
    };

    const formMethods = useForm<EditMatchForm>({
        mode: "onChange",
        defaultValues: {
            activityType: matchPhone?.isTraining ? "training" : "match",
            myTeamName: stripGenericTeamName(initialMapping.myTeam.name, true),
            myTeamShort: initialMapping.myTeam.short,
            myTeamColor: initialMapping.myTeam.color,
            opponentName: stripGenericTeamName(initialMapping.opponent.name),
            opponentShort: initialMapping.opponent.short,
            opponentColor: initialMapping.opponent.color,
            myTeamScore: initialScores.myScore,
            opponentScore: initialScores.opponentScore,
            myClaims: matchPhone?.myClaims ?? NO_CLAIMS,
            location: initialMatchLocation,
            venue: stripWatchPlaceholder(matchPhone?.venue),
            competition: stripWatchPlaceholder(matchPhone?.competition),
            playerPosition: matchPhone?.playerPosition || (profile?.position ?? undefined),
        },
    });

    const { handleSubmit, setValue, watch, formState, reset, getValues, trigger } = formMethods;
    const [presentToast] = useIonToast();

    // Watch for form changes
    const location = useWatch({ control: formMethods.control, name: "location" });
    const myTeamName = useWatch({ control: formMethods.control, name: "myTeamName" });
    const isTraining = useWatch({ control: formMethods.control, name: "activityType" }) === "training";

    // Training hides the match fields; hidden fields are unmounted, which
    // react-hook-form skips when validating, so re-run it to refresh isValid.
    useEffect(() => {
        const timeoutId = setTimeout(() => trigger(), 0);
        return () => clearTimeout(timeoutId);
    }, [isTraining, trigger]);

    // Update default values when match loads
    useEffect(() => {
        if (matchPhone && loading) {
            const loc = matchPhone.matchLocation || MatchLocation.home;
            const mapping = getTeamMapping(loc);
            const scores = getScores(loc);
            const resolvedMyTeamName = stripGenericTeamName(mapping.myTeam.name, true);
            // Regenerate short name when a generic team name was replaced with the profile default
            const resolvedMyTeamShort = resolvedMyTeamName !== mapping.myTeam.name
                ? makeDifferentShortName(resolvedMyTeamName, mapping.opponent.short || makeShortName(stripGenericTeamName(mapping.opponent.name)))
                : mapping.myTeam.short;
            reset({
                activityType: matchPhone.isTraining ? "training" : "match",
                myTeamName: resolvedMyTeamName,
                myTeamShort: resolvedMyTeamShort,
                myTeamColor: mapping.myTeam.color,
                opponentName: stripGenericTeamName(mapping.opponent.name),
                opponentShort: mapping.opponent.short,
                opponentColor: mapping.opponent.color,
                myTeamScore: scores.myScore,
                opponentScore: scores.opponentScore,
                myClaims: matchPhone.myClaims ?? NO_CLAIMS,
                location: loc,
                venue: stripWatchPlaceholder(matchPhone.venue),
                competition: stripWatchPlaceholder(matchPhone.competition),
                playerPosition: matchPhone.playerPosition || (profile?.position ?? undefined),
            });
            setLoading(false);
        }
    }, [matchPhone, loading, reset]);

    // Apply profile team name once after initial load if the form still
    // shows a generic watch value (handles profile loading after matchPhone).
    const [profileApplied, setProfileApplied] = useState(false);
    useEffect(() => {
        if (profile?.teamName && !loading && !profileApplied) {
            const current = getValues("myTeamName");
            if (!current || GENERIC_TEAM_NAMES.includes(current)) {
                setValue("myTeamName", profile.teamName, { shouldValidate: true });
                const opponentShort = getValues("opponentShort");
                setValue("myTeamShort", makeDifferentShortName(profile.teamName, opponentShort));
            }
            setProfileApplied(true);
        }
    }, [profile?.teamName, loading, profileApplied, getValues, setValue, GENERIC_TEAM_NAMES]);

    // Trigger validation so formState.isValid is computed from default/reset values.
    // Runs after mount and whenever matchPhone changes (e.g. after reset).
    useEffect(() => {
        if (matchPhone) {
            const timeoutId = setTimeout(() => trigger(), 0);
            return () => clearTimeout(timeoutId);
        }
    }, [matchPhone, trigger]);

    // Generate a short name that differs from the other team's short name
    const makeDifferentShortName = (teamName: string, otherShortName: string): string => {
        const shortName = makeShortName(teamName);
        if (shortName && shortName === otherShortName) {
            // Append a digit to differentiate, matching REFSIX convention
            return shortName.substring(0, 2) + "1";
        }
        return shortName;
    };

    // Auto-generate short name when team name changes
    useEffect(() => {
        const { dirtyFields } = formState;
        if (myTeamName && dirtyFields.myTeamName && !dirtyFields.myTeamShort) {
            const opponentShort = getValues("opponentShort");
            setValue("myTeamShort", makeDifferentShortName(myTeamName, opponentShort));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myTeamName, setValue]);

    const opponentName = useWatch({ control: formMethods.control, name: "opponentName" });
    useEffect(() => {
        const { dirtyFields } = formState;
        if (opponentName && dirtyFields.opponentName && !dirtyFields.opponentShort) {
            const myTeamShort = getValues("myTeamShort");
            setValue("opponentShort", makeDifferentShortName(opponentName, myTeamShort));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opponentName, setValue]);

    // Watch team colors
    const myTeamColor = watch("myTeamColor");
    const opponentColor = watch("opponentColor");

    // Color picker handlers
    const openColorPicker = (team: MatchLocation) => {
        setColorPickerTeam(team);
        setShowColorPicker(true);
    };

    const handleColorPicked = (color: string) => {
        if (colorPickerTeam === MatchLocation.home) {
            setValue("myTeamColor", color);
        } else {
            setValue("opponentColor", color);
        }
        setShowColorPicker(false);
    };

    // Rebuild both teams' goal events from the scores
    const updateScoreEvents = (matchPhone: MatchPhone, data: EditMatchForm) => {
        const mySide = sideForLocation(data.location);
        const opponentSide = mySide === SelectedTeam.home ? SelectedTeam.away : SelectedTeam.home;
        const newGoal = (side: SelectedTeam, teamName: string, shortName: string) => (timestamp: number) =>
            ({
                timestamp,
                eventName: EventName.goal,
                ownGoal: false,
                penalty: false,
                freeKick: false,
                minuteOfPlay: 1,
                half: 1,
                player: {
                    name: t("fixture.FixtureTeam.unknownPlayer"),
                    number: 1,
                    captain: false,
                    starting: true,
                },
                team: { side, teamName, shortName },
            } as GoalEvent);

        const withMyGoals = setGoalsForSide(
            matchPhone.matchEvents || {},
            mySide,
            data.myTeamScore,
            newGoal(mySide, data.myTeamName, data.myTeamShort)
        );
        return setGoalsForSide(
            withMyGoals,
            opponentSide,
            data.opponentScore,
            newGoal(opponentSide, data.opponentName, data.opponentShort)
        );
    };

    // Claims can't outnumber the team's goals; say so when a lower score takes one
    const changeMyTeamScore = (score: number) => {
        const { claims, dropped } = trimClaimsToScore(getValues("myClaims"), score);
        setValue("myTeamScore", score);
        setValue("myClaims", claims);
        if (dropped) {
            presentToast({
                message: t(
                    dropped === "goals"
                        ? "editMatch.claimRack.goalClaimRemoved"
                        : "editMatch.claimRack.assistClaimRemoved"
                ),
                duration: 3000,
                position: "bottom",
            });
        }
    };

    const leave = () => matchPhone && router.push(`${routes.match}/${matchPhone._id}/overview`, "back");

    const saveMatch = async (updatedMatch: MatchPhone, earned?: { moment: SaveMoment; score: string }) => {
        if (!matchPhone) return;
        try {
            const savedMatch = await updateMatch(updatedMatch);
            if (savedMatch && profile) {
                await recalculateStats(savedMatch, profile);
            }
            if (savedMatch?.isTraining) {
                // SCORZA-766: withdraws it from the board it was filed on as a match, if any.
                await submitMatchAndTagVenue(savedMatch);
            }
            if (savedMatch) {
                trackMatchEdited(savedMatch);
            }
            if (earned) {
                setSplash(earned);
            } else {
                leave();
            }
        } catch (error) {
            console.error("Failed to save match:", error);
            presentToast({
                message: t("general.errorSaving"),
                duration: 3000,
                color: "danger",
                position: "bottom",
            });
        }
    };

    const onSubmit = async (data: EditMatchForm) => {
        if (!matchPhone) return;

        if (data.activityType === "training") {
            await saveMatch(markAsTraining({ ...matchPhone, detailsConfirmed: true }));
            return;
        }

        // Map form data back to home/away based on match location
        const matchLocation = data.location;

        // Compute short names from the final team names (short names are not user-editable)
        const myTeamShort = makeDifferentShortName(data.myTeamName, makeShortName(data.opponentName));
        const opponentShort = makeDifferentShortName(data.opponentName, myTeamShort);

        let updatedMatch: MatchPhone;
        if (matchLocation === MatchLocation.home) {
            updatedMatch = {
                ...matchPhone,
                homeTeam: data.myTeamName,
                homeTeamShort: myTeamShort,
                homeColor: data.myTeamColor,
                awayTeam: data.opponentName,
                awayTeamShort: opponentShort,
                awayColor: data.opponentColor,
                venue: data.venue,
                competition: data.competition,
                matchLocation: MatchLocation.home,
                playerPosition: data.playerPosition,
            };
        } else {
            updatedMatch = {
                ...matchPhone,
                awayTeam: data.myTeamName,
                awayTeamShort: myTeamShort,
                awayColor: data.myTeamColor,
                homeTeam: data.opponentName,
                homeTeamShort: opponentShort,
                homeColor: data.opponentColor,
                venue: data.venue,
                competition: data.competition,
                matchLocation: MatchLocation.away,
                playerPosition: data.playerPosition,
            };
        }

        updatedMatch.isTraining = false;
        updatedMatch.detailsConfirmed = true;

        // Deep clone matchEvents to avoid mutating Redux/store state
        updatedMatch.matchEvents = cloneDeep(matchPhone.matchEvents) || {};

        // Detect location swap
        const originalLocation = matchPhone.matchLocation || MatchLocation.home;
        const locationSwapped = originalLocation !== matchLocation;

        // When location is swapped, swap event.team.side so it tracks the actual team
        if (locationSwapped && updatedMatch.matchEvents) {
            for (const key of Object.keys(updatedMatch.matchEvents)) {
                const event: any = updatedMatch.matchEvents[key];
                if (event.team?.side === SelectedTeam.home) {
                    event.team.side = SelectedTeam.away;
                } else if (event.team?.side === SelectedTeam.away) {
                    event.team.side = SelectedTeam.home;
                }
            }
        }

        // Update team names in the cloned events
        updateTeamNameInEvents(
            updatedMatch,
            matchPhone.homeTeam,
            matchPhone.awayTeam,
            updatedMatch.homeTeam,
            updatedMatch.homeTeamShort,
            updatedMatch.awayTeam,
            updatedMatch.awayTeamShort
        );

        // Move existing players onto new team names, following the team through location swaps
        const originalHomePlayers = cloneDeep(matchPhone.players?.[matchPhone.homeTeam] || []);
        const originalAwayPlayers = cloneDeep(matchPhone.players?.[matchPhone.awayTeam] || []);
        updatedMatch.players = {
            [updatedMatch.homeTeam]: locationSwapped ? originalAwayPlayers : originalHomePlayers,
            [updatedMatch.awayTeam]: locationSwapped ? originalHomePlayers : originalAwayPlayers,
        };

        // Update goal events from the scores (operates on the cloned events).
        // Use updatedMatch (not matchPhone) so the goals are read with the
        // already-swapped team.side values when the user toggled location.
        updatedMatch.matchEvents = updateScoreEvents(updatedMatch, data);
        updatedMatch.myClaims = data.myClaims;

        const moment = earnedMoment(
            {
                goals: (matchPhone.myClaims ?? NO_CLAIMS).goals,
                goalkeeper: matchPhone.playerPosition === FootballPosition.GK,
                conceded: initialScores.opponentScore,
                confirmed: !!matchPhone.detailsConfirmed,
            },
            {
                goals: data.myClaims.goals,
                goalkeeper: data.playerPosition === FootballPosition.GK,
                conceded: data.opponentScore,
                confirmed: true,
            }
        );
        const score = `${data.myTeamName} ${data.myTeamScore}–${data.opponentScore} ${data.opponentName}`;
        await saveMatch(updatedMatch, moment && { moment, score });
    };

    const myTeamScore = watch("myTeamScore");
    const opponentScore = watch("opponentScore");
    const myClaims = watch("myClaims");

    return (
        <IonPage>
            <HeaderComponent
                showBackButton
                defaultHref={matchPhone ? `${routes.match}/${matchPhone._id}/overview` : routes.tabResults}
                title={t("general.matchDetails")}
                titleTestId="edit-match-details-title"
            />
            <IonContent className="edit-match-content">
                <IonLoading isOpen={loading} />
                <FormProvider {...formMethods}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {/* Training / Match */}
                        <IonCard className="edit-match-card">
                            <div className="location-row activity-type-row" data-testid="activityTypeSegment">
                                <div className="location-segment activity-type-segment">
                                    <IonFormSegment name="activityType" value="match">
                                        <IonSegmentButton value="training" data-testid="activityTypeTraining">
                                            <IonLabel>{t("editMatch.training")}</IonLabel>
                                        </IonSegmentButton>
                                        <IonSegmentButton value="match" data-testid="activityTypeMatch">
                                            <IonLabel>{t("editMatch.match")}</IonLabel>
                                        </IonSegmentButton>
                                    </IonFormSegment>
                                </div>
                            </div>
                        </IonCard>

                        {!isTraining && (
                            <>
                                {/* Team Score Header */}
                                <IonCard className="team-score-header">
                                    <div className="team-score-row">
                                        <IonIcon
                                            icon={myTeamColor ? shirt : shirtOutline}
                                            className="jersey-icon clickable"
                                            style={myTeamColor ? { color: myTeamColor } : undefined}
                                            onClick={() => openColorPicker(MatchLocation.home)}
                                            data-testid="myTeamColor"
                                        />
                                        <div className="team-info">
                                            <Controller
                                                name="myTeamName"
                                                control={formMethods.control}
                                                rules={{
                                                    required: t("editMatch.fieldRequired", { fieldName: t("general.team") }) as string,
                                                    validate: (value) => {
                                                        const opponent = getValues("opponentName");
                                                        if (value && opponent && value.trim() === opponent.trim()) {
                                                            return t("editMatch.teamNamesMustDiffer") as string;
                                                        }
                                                        return true;
                                                    },
                                                }}
                                                render={({ field, fieldState }) => (
                                                    <>
                                                        <IonInput
                                                            className="team-name-input"
                                                            value={field.value}
                                                            onIonInput={(e) => {
                                                                field.onChange(e.detail.value);
                                                                trigger("opponentName");
                                                            }}
                                                            label={t("editMatch.myTeam")}
                                                            labelPlacement="floating"
                                                            data-testid="myTeamNameInput"
                                                        />
                                                        {fieldState.error && (
                                                            <div className="field-error-text">{fieldState.error.message}</div>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </div>
                                        <ScoreCounter
                                            value={myTeamScore}
                                            onChange={changeMyTeamScore}
                                            testId="myTeamScore"
                                        />
                                    </div>
                                    <div className="team-score-row">
                                        <IonIcon
                                            icon={opponentColor ? shirt : shirtOutline}
                                            className="jersey-icon clickable"
                                            style={opponentColor ? { color: opponentColor } : undefined}
                                            onClick={() => openColorPicker(MatchLocation.away)}
                                            data-testid="opponentColor"
                                        />
                                        <div className="team-info">
                                            <Controller
                                                name="opponentName"
                                                control={formMethods.control}
                                                rules={{
                                                    required: t("editMatch.fieldRequired", { fieldName: t("general.team") }) as string,
                                                    validate: (value) => {
                                                        const myTeam = getValues("myTeamName");
                                                        if (value && myTeam && value.trim() === myTeam.trim()) {
                                                            return t("editMatch.teamNamesMustDiffer") as string;
                                                        }
                                                        return true;
                                                    },
                                                }}
                                                render={({ field, fieldState }) => (
                                                    <>
                                                        <IonInput
                                                            className="team-name-input"
                                                            value={field.value}
                                                            onIonInput={(e) => {
                                                                field.onChange(e.detail.value);
                                                                trigger("myTeamName");
                                                            }}
                                                            label={t("editMatch.opponent")}
                                                            labelPlacement="floating"
                                                            data-testid="opponentNameInput"
                                                        />
                                                        {fieldState.error && (
                                                            <div className="field-error-text">{fieldState.error.message}</div>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </div>
                                        <ScoreCounter
                                            value={opponentScore}
                                            onChange={(val) => setValue("opponentScore", val)}
                                            testId="opponentScore"
                                        />
                                    </div>
                                </IonCard>

                                {myTeamScore > 0 && (
                                    <ClaimRows
                                        claims={myClaims}
                                        teamGoals={myTeamScore}
                                        season={seasonClaims}
                                        onChange={(claims) => setValue("myClaims", claims)}
                                    />
                                )}

                                {/* Match Info Section */}
                                <IonCard className="edit-match-card">
                                    <div className="location-row">
                                        <IonIcon
                                            icon={locationPitchIcon}
                                            className="location-icon"
                                        />
                                        <span className="location-label">
                                            {t("editMatch.location")}
                                        </span>
                                        <div className="location-segment">
                                            <IonFormSegment name="location" value={location}>
                                                <IonSegmentButton value="home">
                                                    <IonLabel>{t("general.home")}</IonLabel>
                                                </IonSegmentButton>
                                                <IonSegmentButton value="away">
                                                    <IonLabel>{t("general.away")}</IonLabel>
                                                </IonSegmentButton>
                                            </IonFormSegment>
                                        </div>
                                    </div>

                                    {/* Venue Row */}
                                    <div className="field-row">
                                        <IonIcon
                                            icon={venueStadiumIcon}
                                            className="field-icon"
                                        />
                                        <div className="field-info">
                                            <Controller
                                                name="venue"
                                                control={formMethods.control}
                                                rules={{
                                                    required: t("editMatch.fieldRequired", { fieldName: t("fixture.FixtureNew.venue") }) as string,
                                                }}
                                                render={({ field, fieldState }) => (
                                                    <>
                                                        <IonInput
                                                            className="field-input"
                                                            value={field.value}
                                                            onIonInput={(e) => field.onChange(e.detail.value)}
                                                            label={t("fixture.FixtureNew.venue")}
                                                            labelPlacement="floating"
                                                            data-testid="venueInput"
                                                        />
                                                        {fieldState.error && (
                                                            <div className="field-error-text">{fieldState.error.message}</div>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </div>
                                    </div>

                                    {/* Competition Row */}
                                    <div className="field-row">
                                        <IonIcon
                                            icon={competitionTrophyIcon}
                                            className="field-icon"
                                        />
                                        <div className="field-info">
                                            <Controller
                                                name="competition"
                                                control={formMethods.control}
                                                rules={{
                                                    required: t("editMatch.fieldRequired", { fieldName: t("fixture.FixtureNew.competition") }) as string,
                                                }}
                                                render={({ field, fieldState }) => (
                                                    <>
                                                        <IonInput
                                                            className="field-input"
                                                            value={field.value}
                                                            onIonInput={(e) => field.onChange(e.detail.value)}
                                                            label={t("fixture.FixtureNew.competition")}
                                                            labelPlacement="floating"
                                                            data-testid="competitionInput"
                                                        />
                                                        {fieldState.error && (
                                                            <div className="field-error-text">{fieldState.error.message}</div>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </div>
                                    </div>

                                    {/* Position Row */}
                                    <div className="field-row">
                                        <IonIcon
                                            icon={positionPlayerIcon}
                                            className="field-icon"
                                        />
                                        <div className={`field-info ${formState.errors.playerPosition ? "field-error" : ""}`}>
                                            <PositionDropdown
                                                name="playerPosition"
                                                label={t("editMatch.position")}
                                                labelPlacement="stacked"
                                                testId="playerPositionSelect"
                                                className="field-dropdown"
                                                slot=""
                                                rules={{
                                                    required: t("editMatch.fieldRequired", { fieldName: t("editMatch.position") }) as string,
                                                }}
                                            />
                                            {formState.errors.playerPosition && (
                                                <div className="field-error-text" data-testid="positionError">
                                                    {formState.errors.playerPosition.message}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </IonCard>
                            </>
                        )}

                        <input type="submit" hidden />
                    </form>

                    {/* Save Button */}
                    <div className="save-button-container">
                        <IonButton
                            expand="block"
                            className="save-button"
                            color="primary"
                            onClick={() => handleSubmit(onSubmit)()}
                            data-testid="saveMatchBtn"
                            disabled={!formState.isValid || undefined}
                        >
                            {t("general.save")}
                        </IonButton>
                    </div>
                </FormProvider>

                {/* Space for keyboard */}
                <div style={{ height: "100px" }}></div>

                {/* Color Picker Modal */}
                <IonModal
                    isOpen={showColorPicker}
                    onDidDismiss={() => setShowColorPicker(false)}
                    className="color-picker-modal"
                >
                    <div className="color-picker-modal-content">
                        <div className="color-picker-modal-header">
                            <span className="color-picker-modal-title">{t("editMatch.teamColour")}</span>
                            <IonButton fill="clear" onClick={() => setShowColorPicker(false)}>
                                <IonIcon icon={closeOutline} />
                            </IonButton>
                        </div>
                        <ColorPickerComponent
                            colorPicked={handleColorPicked}
                            selectedColor={colorPickerTeam === MatchLocation.home ? myTeamColor : opponentColor}
                        />
                    </div>
                </IonModal>
            </IonContent>
            {splash && <SaveSplash moment={splash.moment} score={splash.score} onDone={leave} />}
        </IonPage>
    );
};

export default EditMatchDetails;
